import { baseApi } from "./baseApi";
import { supabase } from "../lib/supabaseClient";

export const watchPartyApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getWatchParties: builder.query<any[], { filter: "today" | "upcoming" | "all"; limit: number; userId?: string }>({
      queryFn: async ({ filter, limit, userId }) => {
        try {
          let query = supabase.from("watch_parties").select("*").order("scheduled_time", { ascending: false });

          const now = new Date();
          if (filter === "today") {
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const end = new Date(start);
            end.setDate(end.getDate() + 1);
            query = query.gte("scheduled_time", start.toISOString()).lt("scheduled_time", end.toISOString());
          } else if (filter === "upcoming") {
            query = query.gte("scheduled_time", now.toISOString());
          }

          query = query.limit(limit);
          const { data: parties, error } = await query;
          if (error) throw error;

          // Fetch additional info for each party
          const partiesWithInfo = await Promise.all(
            (parties || []).map(async (party: any) => {
              const [{ count }, { data: isJoinedData }, { data: hostProfile }, { data: filmData }] = await Promise.all([
                supabase.from("watch_party_participants").select("*", { count: "exact", head: true }).eq("watch_party_id", party.id),
                userId
                  ? supabase.from("watch_party_participants").select("id").eq("watch_party_id", party.id).eq("user_id", userId).maybeSingle()
                  : Promise.resolve({ data: null }),
                supabase.from("user_profiles").select("*").eq("id", party.host_user_id).single(),
                supabase.from("films").select("*").eq("imdb_id", party.imdb_id).single(),
              ]);

              return {
                ...party,
                participants_count: count || 0,
                is_joined: !!isJoinedData,
                host_profile: hostProfile,
                film: filmData,
              };
            })
          );

          return { data: partiesWithInfo };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      providesTags: ["WatchParty"],
    }),
    getWatchParty: builder.query<any, { partyId: string; userId?: string }>({
      queryFn: async ({ partyId, userId }) => {
        try {
          const { data: party, error } = await supabase.from("watch_parties").select("*").eq("id", partyId).single();
          if (error) throw error;

          // Fetch participants with profiles
          const { data: participants } = await supabase
            .from("watch_party_participants")
            .select("user_id, joined_at")
            .eq("watch_party_id", partyId);

          let participantsData: any[] = [];
          if (participants && participants.length > 0) {
            const participantIds = participants.map((p: any) => p.user_id);
            const { data: profiles } = await supabase.from("user_profiles").select("*").in("id", participantIds);
            participantsData = participants.map((p: any) => {
              const profile = profiles?.find((prof: any) => prof.id === p.user_id);
              // Spread profile data first, then participant data to ensure we have all profile fields
              return profile ? { ...profile, ...p } : { ...p, username: "Unknown", avatar_url: null };
            });
          }

          // Check if user is joined
          const isJoined = userId ? participantsData.some((p: any) => p.user_id === userId) : false;

          // Fetch host profile and film
          const [{ data: hostProfile }, { data: film }] = await Promise.all([
            supabase.from("user_profiles").select("*").eq("id", party.host_user_id).single(),
            supabase.from("films").select("*").eq("imdb_id", party.imdb_id).single(),
          ]);

          return {
            data: {
              ...party,
              participants: participantsData,
              participants_count: participantsData.length,
              is_joined: isJoined,
              host_profile: hostProfile,
              film,
            },
          };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      providesTags: (result, error, arg) => [{ type: "WatchParty", id: arg.partyId }],
    }),
    createWatchParty: builder.mutation<any, any>({
      queryFn: async (partyData) => {
        try {
          const { data, error } = await supabase.from("watch_parties").insert([partyData]).select().single();
          if (error) throw error;

          // Auto-join host as participant
          const { error: joinError } = await supabase.from("watch_party_participants").insert([
            {
              watch_party_id: data.id,
              user_id: partyData.host_user_id,
            },
          ]);

          if (joinError) throw joinError;

          return { data };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      invalidatesTags: ["WatchParty"],
    }),
    joinWatchParty: builder.mutation<void, { partyId: string; userId: string }>({
      queryFn: async ({ partyId, userId }) => {
        try {
          const { error } = await supabase.from("watch_party_participants").insert([{ watch_party_id: partyId, user_id: userId }]);
          if (error) throw error;
          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: "WatchParty", id: arg.partyId }, "WatchParty"],
    }),
    leaveWatchParty: builder.mutation<void, { partyId: string; userId: string }>({
      queryFn: async ({ partyId, userId }) => {
        try {
          const { error } = await supabase.from("watch_party_participants").delete().eq("watch_party_id", partyId).eq("user_id", userId);
          if (error) throw error;
          return { data: undefined };
        } catch (error: any) {
          return { error: { status: 500, statusText: error.message, data: error } };
        }
      },
      invalidatesTags: (result, error, arg) => [{ type: "WatchParty", id: arg.partyId }, "WatchParty"],
    }),
  }),
});

export const {
  useGetWatchPartiesQuery,
  useGetWatchPartyQuery,
  useCreateWatchPartyMutation,
  useJoinWatchPartyMutation,
  useLeaveWatchPartyMutation,
} = watchPartyApi;
